import React, { useState, useEffect } from 'react';
import { Spinner, Modal, Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { dsoApi } from '../../services/api';
import { FaUser, FaKey, FaBell, FaShieldAlt, FaFileAlt, FaIdCard, FaFileInvoice, FaBuilding, FaFileContract, FaUniversity, FaFilePdf } from 'react-icons/fa';
import ServiceAgreement from '../../components/ServiceAgreement';
import './Settings.css';

function DSOSettings() {
  const [activeSection, setActiveSection] = useState('profile');
  const [dsoDetails, setDsoDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState(true);
  
  // Form data for editing profile
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    profile_data: {
      address: '',
      adhaar_card_no: '',
      pan_card_no: '',
      gstin_no: '',
      city: '',
      state: ''
    },
    bank_details: {
      bank_name: '',
      bank_account_no: '',
      bank_ifsc_code: '',
      bank_branch_name: '',
      bank_account_type: '1', // Default to Savings Account
      upi_id: ''
    }
  });
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Add a new state for bank form submission
  const [bankUpdateSuccess, setBankUpdateSuccess] = useState('');
  const [bankUpdateError, setBankUpdateError] = useState('');
  const [bankSaveLoading, setBankSaveLoading] = useState(false);

  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Modal handler functions
  const handleCloseModal = () => {
    setShowResetModal(false);
    // Reset modal-specific states
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess('');
    setResetLoading(false);
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 5) {
      setResetError('Password must be at least 5 characters long.');
      return;
    }

    setResetLoading(true);
    try {
      await dsoApi.changePassword(newPassword);
      setResetSuccess('Password reset successfully!');
    } catch (err) {
      console.error("Error resetting password:", err);
      setResetError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Navigation items for settings
  const navItems = [
    { id: 'profile', label: 'Profile Information', icon: <FaUser size={16} /> },
    { id: 'documents', label: 'Documents', icon: <FaFileAlt size={16} /> },
    { id: 'agreement', label: 'Agreement', icon: <FaFileContract size={16} /> }, // Moved Agreement to 3rd position
    { id: 'bank', label: 'Bank Details', icon: <FaUniversity size={16} /> },
    { id: 'security', label: 'Security', icon: <FaKey size={16} /> },
    { id: 'notifications', label: 'Notifications', icon: <FaBell size={16} /> }
  ];
  
  // Toggle handlers for notifications
  const toggleNotifications = () => {
    setNotifications(!notifications);
  };


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested fields (profile_data or bank_details)
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Handle top-level fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Submit profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdateError('');
    setUpdateSuccess('');
    setSaveLoading(true);

    try {
      const response = await dsoApi.updateDsoProfile(formData);
      setDsoDetails(response.data);
      setUpdateSuccess('Profile updated successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess('');
      }, 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setUpdateError(err.response?.data?.detail || 'Failed to update profile. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Handle bank details form submission
  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();
    setBankUpdateError('');
    setBankUpdateSuccess('');
    setBankSaveLoading(true);

    try {
      const response = await dsoApi.updateDsoBankDetails(formData.bank_details);
      console.log("Bank details updated:", response.data);
      
      // Update the local state with the new data
      setDsoDetails({
        ...dsoDetails,
        dso_profile: {
          ...dsoDetails.dso_profile,
          ...response.data
        }
      });
      
      setBankUpdateSuccess('Bank details updated successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setBankUpdateSuccess('');
      }, 3000);
    } catch (err) {
      console.error("Error updating bank details:", err);
      setBankUpdateError(err.response?.data?.detail || 'Failed to update bank details. Please try again.');
    } finally {
      setBankSaveLoading(false);
    }
  };
  
  const handlePrintServiceAgreement = () => {
    // Create a hidden iframe to avoid affecting the current page layout
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    // Get the iframe's document and write the agreement content
    const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    frameDoc.open();
    
    // Get the agreement content
    const agreementContent = document.querySelector('.service-agreement').innerHTML;
    const companyName = dsoDetails?.name || dsoDetails?.dso_profile?.company_name || "Company";
    
    // Write the document with the agreement content
    frameDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Service Agreement - ${companyName}</title>
          <style>
            body { 
              font-family: 'Calibri', sans-serif;
              padding: 40px; 
            }
            h1 { 
              font-size: 18px; 
              font-weight: bold; 
              text-align: center;
            }
            h2 { 
              font-size: 16px; 
              font-weight: bold; 
              margin-top: 16px;
            }
            p { 
              margin-bottom: 10px; 
              text-align: justify;
            }
            .centered { 
              text-align: center; 
            }
            .bold { 
              font-weight: bold; 
            }
            .underline { 
              text-decoration: underline; 
            }
            .section { 
              margin-bottom: 20px; 
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            table th, table td {
              border: 1px solid #000;
              padding: 8px 12px;
              text-align: left;
            }
            .signature-section {
              display: flex;
              justify-content: space-between;
              margin-top: 40px;
              page-break-inside: avoid;
            }
            .signature-block {
              width: 45%;
              border: 1px solid #000;
            }
            .signature-title {
              border-bottom: 1px solid #000;
              padding: 10px;
              font-weight: bold;
            }
            .signature-space {
              height: 80px;
              border-bottom: 1px solid #000;
            }
            .signature-info {
              padding: 10px;
            }
            @page { 
              size: A4; 
              margin: 2cm; 
            }
          </style>
        </head>
        <body>
          <div>
            ${agreementContent}
          </div>
        </body>
      </html>
    `);
    frameDoc.close();
    
    // Wait for the content to load and then print
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      // Remove the iframe after printing or after some time
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }, 500);
  };

  // Fetch DSO details
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError('');
      try {
        console.log("Attempting to fetch DSO details...");
        const response = await dsoApi.getDsoProfile();
        console.log("DSO details fetched:", response.data);
        setDsoDetails(response.data);
        
        // Initialize form data with fetched details
        if (response.data) {
          setFormData({
            name: response.data.name || '',
            mobile: response.data.mobile || '',
            profile_data: {
              address: response.data.dso_profile?.address || '',
              adhaar_card_no: response.data.dso_profile?.adhaar_card_no || '',
              pan_card_no: response.data.dso_profile?.pan_card_no || '',
              gstin_no: response.data.dso_profile?.gstin_no || '',
              city: response.data.dso_profile?.city_name || '',
              state: response.data.dso_profile?.state_name || ''
            },
            bank_details: {
              bank_name: response.data.dso_profile?.bank_name || '',
              bank_account_no: response.data.dso_profile?.bank_account_no || '',
              bank_ifsc_code: response.data.dso_profile?.bank_ifsc_code || '',
              bank_branch_name: response.data.dso_profile?.bank_branch_name || '',
              bank_account_type: response.data.dso_profile?.bank_account_type?.toString() || '1',
              upi_id: response.data.dso_profile?.upi_id || ''
            }
          });
        }
        
        // If your API returns notification preferences, set them here
        if (response.data.preferences) {
          setNotifications(response.data.preferences.notificationEnabled || true);
        }
      } catch (err) {
        console.error("Error fetching DSO details:", err);
        let errorMessage = 'Failed to load profile information.';
        if (err.response) {
          console.error("Error response:", err.response.data);
          errorMessage += ` (Server: ${err.response.data.detail || err.response.statusText})`;
        } else if (err.request) {
          console.error("Error request:", err.request);
          errorMessage += ' (No response from server)';
        } else {
          console.error("Error message:", err.message);
          errorMessage += ` (${err.message})`;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, []);

  // Monitor modal state for debugging
  useEffect(() => {
    console.log(`showResetModal state changed to: ${showResetModal}`);
  }, [showResetModal]);

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        if (loading) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        
        return (
          <>
            <h3>Profile Information</h3>
            
            {updateSuccess && <Alert variant="success">{updateSuccess}</Alert>}
            {updateError && <Alert variant="danger">{updateError}</Alert>}
            
            {dsoDetails ? (
              <Form onSubmit={handleProfileSubmit}>
                <div className="dso-profile-details">
                  <h4 className="dso-section-title">Personal Information</h4>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Name:</span>
                    <Form.Control 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Email:</span>
                    <span className="dso-detail-value">{dsoDetails.email || 'N/A'}</span>
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Mobile Number:</span>
                    <Form.Control 
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Status:</span>
                    <span className="dso-detail-value">{dsoDetails.dso_profile?.status || (dsoDetails.is_active ? 'Active' : 'Inactive')}</span>
                  </div>
                  
                  <h4 className="dso-section-title">Location Details</h4>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">City:</span>
                    <Form.Control 
                      name="profile_data.city"
                      value={formData.profile_data.city}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                      disabled
                      title="Contact administrator to update city"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">State:</span>
                    <Form.Control 
                      name="profile_data.state"
                      value={formData.profile_data.state}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                      disabled
                      title="Contact administrator to update state"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Address:</span>
                    <Form.Control 
                      name="profile_data.address"
                      value={formData.profile_data.address}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <h4 className="dso-section-title">Identity Details</h4>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Aadhaar Card:</span>
                    <Form.Control 
                      name="profile_data.adhaar_card_no"
                      value={formData.profile_data.adhaar_card_no}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">PAN Card:</span>
                    <Form.Control 
                      name="profile_data.pan_card_no"
                      value={formData.profile_data.pan_card_no}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">GSTIN Number:</span>
                    <Form.Control 
                      name="profile_data.gstin_no"
                      value={formData.profile_data.gstin_no}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <h4 className="dso-section-title">Business Information</h4>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Applicant Type:</span>
                    <span className="dso-detail-value">{dsoDetails.dso_profile?.applicant_type || 'N/A'}</span>
                  </div>
                  
                  {dsoDetails.dso_profile?.applicant_type === 'Individual' && (
                    <div className="dso-detail-item">
                      <span className="dso-detail-label">Individual Type:</span>
                      <span className="dso-detail-value">{dsoDetails.dso_profile?.individual_type || 'N/A'}</span>
                    </div>
                  )}
                  
                  {dsoDetails.dso_profile?.applicant_type === 'Business' && (
                    <div className="dso-detail-item">
                      <span className="dso-detail-label">Business Type:</span>
                      <span className="dso-detail-value">{dsoDetails.dso_profile?.business_type || 'N/A'}</span>
                    </div>
                  )}
                  
                  <div className="mt-3">
                    <Button 
                      type="submit" 
                      className="dso-btn dso-btn-primary" 
                      disabled={saveLoading}
                    >
                      {saveLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                        </>
                      ) : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <p>Could not load profile data.</p>
            )}
          </>
        );
        
      case 'documents':
        return (
          <>
            <h3>Your Documents</h3>
            <div className="dso-document-grid">
              {dsoDetails ? (
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <div className="document-icon">
                          <FaIdCard size={30} color="#0a2540" />
                        </div>
                        <Card.Title>Aadhaar Card (Front)</Card.Title>
                        {dsoDetails.dso_profile?.adhaar_card_front_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                            </div>
                            <div className="document-preview">
                              {dsoDetails.dso_profile.adhaar_card_front_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={dsoDetails.dso_profile.adhaar_card_front_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={dsoDetails.dso_profile.adhaar_card_front_file} 
                                  alt="Aadhaar Front" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="document-status document-missing">
                            Not Uploaded
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <div className="document-icon">
                          <FaIdCard size={30} color="#0a2540" />
                        </div>
                        <Card.Title>Aadhaar Card (Back)</Card.Title>
                        {dsoDetails.dso_profile?.adhaar_card_back_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                            </div>
                            <div className="document-preview">
                              {dsoDetails.dso_profile.adhaar_card_back_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={dsoDetails.dso_profile.adhaar_card_back_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={dsoDetails.dso_profile.adhaar_card_back_file} 
                                  alt="Aadhaar Back" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="document-status document-missing">
                            Not Uploaded
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <div className="document-icon">
                          <FaFileInvoice size={30} color="#0a2540" />
                        </div>
                        <Card.Title>PAN Card</Card.Title>
                        {dsoDetails.dso_profile?.pan_card_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                              {dsoDetails.dso_profile?.pan_verified && 
                                <span className="verification-badge">Verified</span>
                              }
                            </div>
                            <div className="document-preview">
                              {dsoDetails.dso_profile.pan_card_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={dsoDetails.dso_profile.pan_card_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={dsoDetails.dso_profile.pan_card_file} 
                                  alt="PAN Card" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="document-status document-missing">
                            Not Uploaded
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  {dsoDetails.dso_profile?.applicant_type === 'Business' && (
                    <Col md={6}>
                      <Card className="dso-document-card h-100">
                        <Card.Body>
                          <div className="document-icon">
                            <FaBuilding size={30} color="#0a2540" />
                          </div>
                          <Card.Title>Company Certificate</Card.Title>
                          {dsoDetails.dso_profile?.company_certificate_file ? (
                            <>
                              <div className="document-status document-uploaded">
                                Document Uploaded
                              </div>
                              <div className="document-preview">
                                {dsoDetails.dso_profile.company_certificate_file.toLowerCase().endsWith('.pdf') ? (
                                  <a 
                                    href={dsoDetails.dso_profile.company_certificate_file} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="pdf-link"
                                  >
                                    <FaFileAlt size={40} />
                                    <div>View PDF Document</div>
                                  </a>
                                ) : (
                                  <img 
                                    src={dsoDetails.dso_profile.company_certificate_file} 
                                    alt="Company Certificate" 
                                    className="document-thumbnail" 
                                  />
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="document-status document-missing">
                              Not Uploaded
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>
              ) : (
                <p>Could not load document data.</p>
              )}
            </div>
          </>
        );
        
      case 'security':
        console.log("Rendering 'security' section content.");
        return (
          <>
            <h3>Security Settings</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Password:</span>
                <button
                  className="dso-btn dso-btn-primary"
                  onClick={() => {
                    console.log("Reset Password button clicked!");
                    setShowResetModal(true);
                  }}
                >
                  <FaKey/> Reset Password
                </button>
              </div>
            </div>
          </>
        );
        
      case 'notifications':
        return (
          <>
            <h3>Notification Preferences</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Notifications:</span>
                <div className="dso-toggle-container">
                  <label className="dso-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifications} 
                      onChange={toggleNotifications} 
                    />
                    <span className="dso-toggle-slider"></span>
                  </label>
                  <span className="dso-toggle-status">
                    {notifications ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </>
        );
        
      case 'agreement':
        // Get signed_on date from dsoDetails.dso_profile if available
        const signedOnDate = dsoDetails?.dso_profile?.agreement_signed_on
          ? new Date(dsoDetails.dso_profile.agreement_signed_on).toLocaleDateString()
          : 'N/A';

        return (
          <>
            <div className="d-flex justify-content-between align-items-center">
              <h3>Agreement Details</h3>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handlePrintServiceAgreement}
              >
                <FaFilePdf className="me-2" /> Save as PDF
              </Button>
            </div>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Signed On:</span>
                <span className="dso-detail-value">{signedOnDate}</span>
              </div>
              <div className="dso-detail-item">
                <span className="dso-detail-label">Agreement Status:</span>
                <span className="dso-detail-value">{dsoDetails?.dso_profile?.agreement_signed ? 'Signed' : 'Not Signed'}</span>
              </div>
            </div>
            
            <div className="mt-4">
              <Card>
                <Card.Body>
                  <Card.Title>Service Agreement</Card.Title>
                  <ServiceAgreement 
                    companyName={dsoDetails?.name || dsoDetails?.dso_profile?.company_name || "Your Company"} 
                    companyAddress={dsoDetails?.dso_profile?.address || "Your Registered Address"}
                  />
                </Card.Body>
              </Card>
            </div>
          </>
        );
        
      case 'bank':
        if (loading) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        
        return (
          <>
            <h3>Bank Account Details</h3>
            
            {bankUpdateSuccess && <Alert variant="success">{bankUpdateSuccess}</Alert>}
            {bankUpdateError && <Alert variant="danger">{bankUpdateError}</Alert>}
            
            <Form onSubmit={handleBankDetailsSubmit}>
              <div className="dso-profile-details">
                <h4 className="dso-section-title">Bank Information</h4>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">Bank Name:</span>
                  <Form.Control 
                    name="bank_details.bank_name"
                    value={formData.bank_details.bank_name}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                    placeholder="Enter bank name"
                  />
                </div>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">Branch Name:</span>
                  <Form.Control 
                    name="bank_details.bank_branch_name"
                    value={formData.bank_details.bank_branch_name}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                    placeholder="Enter branch name"
                  />
                </div>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">Account Number:</span>
                  <Form.Control 
                    name="bank_details.bank_account_no"
                    value={formData.bank_details.bank_account_no}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                    placeholder="Enter account number"
                  />
                </div>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">IFSC Code:</span>
                  <Form.Control 
                    name="bank_details.bank_ifsc_code"
                    value={formData.bank_details.bank_ifsc_code}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                    placeholder="Enter IFSC code"
                  />
                </div>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">Account Type:</span>
                  <Form.Select
                    name="bank_details.bank_account_type"
                    value={formData.bank_details.bank_account_type}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                  >
                    <option value="1">Savings Account</option>
                    <option value="2">Current Account</option>
                  </Form.Select>
                </div>
                
                <div className="dso-detail-item">
                  <span className="dso-detail-label">UPI ID:</span>
                  <Form.Control 
                    name="bank_details.upi_id"
                    value={formData.bank_details.upi_id}
                    onChange={handleInputChange}
                    className="dso-detail-value"
                    placeholder="Enter UPI ID (optional)"
                  />
                </div>
                
                <div className="alert alert-info mt-3">
                  <small>
                    <strong>Note:</strong> Bank account details are required for commission payouts. 
                    Please ensure the information is accurate as it will be verified.
                  </small>
                </div>
                
                <div className="mt-3">
                  <Button 
                    type="submit" 
                    className="dso-btn dso-btn-primary" 
                    disabled={bankSaveLoading}
                  >
                    {bankSaveLoading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                      </>
                    ) : "Save Bank Details"}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        );
      default:
        return <p>Select a section from the menu</p>;
    }
  };

  return (
    <div className="dso-settings-page">
      <div className="dso-settings-content">
        {/* Left section - Navigation */}
        <div className="dso-settings-left">
          <ul className="dso-settings-nav">
            {navItems.map(item => (
              <li key={item.id} className="dso-settings-nav-item">
                <div 
                  className={`dso-settings-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <span className="dso-settings-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right section - Content */}
        <div className="dso-settings-right">
          {renderContent()}
        </div>
      </div>

      {/* Reset Password Modal with Custom Styling */}
      <Modal show={showResetModal} onHide={handleCloseModal} centered className="custom-modal">
        <Form onSubmit={handlePasswordResetSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaShieldAlt className="me-2" /> Reset Your Password
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Display Error/Success Messages */}
            {resetError && <Alert variant="danger">{resetError}</Alert>}
            {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}

            {/* Hide form fields after success */}
            {!resetSuccess && (
              <>
                <Form.Group className="mb-4" controlId="formNewPassword">
                  <Form.Label>New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                  <Form.Text className="text-muted">
                    Password must be at least 5 characters long
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="formConfirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={resetLoading}>
              Cancel
            </Button>
            {/* Hide submit button after success */}
            {!resetSuccess && (
              <Button className="dso-btn dso-btn-primary" variant="" type="submit" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default DSOSettings;
